import React, { memo, useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AGENT_DRAG_MIME, type AgentCard } from "../../../../services/agents";
import { agentCategoryIcon, agentCategoryKey } from "./agentCategoryStyle";
import packageStyles from "../toolsMenuPackagePalette/ToolsMenuPackagePalette.module.css";
import rowStyles from "./AgentPaletteRow.module.css";
import { DetailsButton } from "../../../DetailsButton";
import { AgentDetailModal } from "../../../agents/catalog/AgentDetailModal";

/** Human label for a compatible-target kind. */
const HOOK_LABEL: Record<string, string> = {
  canvas: "Canvas",
  node: "Node",
  connection: "Connection",
};

/**
 * One installed-agent row in the AGENTS palette. Mirrors the Datasets/Packages
 * row structure (reusing the shared ``packageKind*`` classes): a draggable,
 * category-tinted avatar (the drag source writing ``AGENT_DRAG_MIME`` so the
 * canvas drop handler can attach it) plus a meta button showing the name, the
 * install coordinate, and a category-colored chip. Clicking the meta opens the
 * catalog drawer; dragging attaches (handled in ``MainCanvas``).
 */
export const AgentPaletteRow = memo(function AgentPaletteRow({
  agent,
  onOpen,
}: {
  agent: AgentCard;
  onOpen?: () => void;
}) {
  const key = agentCategoryKey(agent.category);
  const avatarClass = rowStyles[`avatar_${key}` as keyof typeof rowStyles];
  const tooltip = agent.purpose || agent.capabilities.join(" · ");
  // Show one pill per compatible target kind (Canvas / Node / Connection) so a
  // dual-compatible agent clearly advertises both. Deduped, order preserved.
  const hooks = Array.from(new Set(agent.hooks));

  const [detailsOpen, setDetailsOpen] = useState(false);

  const onDragStart = useCallback(
    (event: React.DragEvent) => {
      event.dataTransfer.setData(AGENT_DRAG_MIME, agent.dirName);
      event.dataTransfer.effectAllowed = "copy";
    },
    [agent.dirName],
  );

  return (
    // The cross-surface identity attribute, matching data-dataset-id on a
    // dataset row and data-package-palette-coords on a package one: a card in
    // the drawer and its row here share one identifier, which is what lets an
    // install be asserted end to end without reading a hashed CSS class.
    <div className={packageStyles.packageKindRow} data-agent-coord={agent.dirName}>
      <div
        className={`${packageStyles.packageKindRowDrag} ${avatarClass}`}
        draggable
        onDragStart={onDragStart}
        aria-label="Drag onto a node or the canvas to attach"
        data-tooltip="Drag onto a node or the canvas to attach"
        data-tooltip-side="right"
      >
        <FontAwesomeIcon icon={agentCategoryIcon(agent.category)} className={rowStyles.avatarIcon} />
      </div>
      <button
        type="button"
        className={packageStyles.packageKindRowMeta}
        onClick={onOpen}
        data-tooltip={tooltip}
        data-tooltip-side="right"
      >
        <span className={packageStyles.packageKindRowLabel}>{agent.name}</span>
        <span className={rowStyles.coord}>{agent.dirName}</span>
        <span className={rowStyles.pills}>
          {hooks.map((h) => {
            const chipClass = rowStyles[`chip_${agentCategoryKey(h)}` as keyof typeof rowStyles];
            return (
              <span
                key={h}
                className={`${packageStyles.packageKindCategoryChip} ${chipClass}`}
              >
                {HOOK_LABEL[h] ?? h}
              </span>
            );
          })}
        </span>
      </button>
      {/* The way into the agent's details, as every catalog card offers. */}
      <DetailsButton
        label={`View ${agent.name} details`}
        className={rowStyles.detailsButton}
        onClick={() => setDetailsOpen(true)}
      />
      {detailsOpen ? (
        <AgentDetailModal agent={agent} onClose={() => setDetailsOpen(false)} />
      ) : null}
    </div>
  );
});
